import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Compass,
  FileCheck,
  Users,
} from "lucide-react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { HashLink } from "react-router-hash-link";

const JourneySteps = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Soft Parallax Motion
  const y = useMotionValue(0);
  const parallax = useTransform(y, [0, 300], [0, -8]); // Soft motion

  // Auto-slide every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === steps.length - 1 ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    {
      icon: <Compass className="w-12 h-12 text-primary-blue" />,
      text: "Find scholarships",
      details: "Explore various scholarship opportunities to fund your studies.",
    },
    {
      icon: <FileCheck className="w-12 h-12 text-primary-brown" />,
      text: "Visa assistance",
      details: "Get expert help with visa applications and requirements.",
    },
    {
      icon: <Users className="w-12 h-12 text-primary" />,
      text: "Get Expert Guidance",
      details: "Receive personalized advice from our team of experts.",
    },
  ];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? steps.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === steps.length - 1 ? 0 : prev + 1));
  };

  return (
    <motion.div
      style={{ y: parallax }}
      onScroll={(e) => y.set(window.scrollY)}
      className="
        w-full max-w-7xl mx-auto p-8 md:p-14 mt-16 
        relative overflow-hidden
        bg-white/40 backdrop-blur-2xl border border-white/30 
        rounded-3xl shadow-[0_12px_45px_rgba(0,0,0,0.08)]
      "
    >
      {/* FLOATING PARTICLES (20) */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-primary/20 rounded-full blur-[3px]"
          initial={{
            x: Math.random() * 700,
            y: Math.random() * 350,
            opacity: 0.3,
          }}
          animate={{
            y: `-${50 + Math.random() * 80}px`,
            x: `${Math.random() * 40}px`,
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 4 + Math.random() * 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      <div className="flex flex-col lg:flex-row items-center gap-16 relative z-10">
        {/* LEFT TEXT BLOCK */}
        <div className="w-full lg:w-1/2 space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-primary leading-snug">
            With you at every step of your study abroad journey
          </h2>

          <p className="text-lg md:text-xl text-primary-dark">
            Get personalised, friendly, honest guidance for free.
          </p>

          <HashLink to="/#contact" smooth>
            <button
              className="px-8 py-4 text-lg font-medium text-white 
            bg-primary-brown rounded-xl shadow-lg hover:shadow-2xl 
            transition-all hover:-translate-y-1"
            >
              Book a free consultation
            </button>
          </HashLink>
        </div>

        {/* RIGHT SLIDER CARD */}
        <div className="w-full lg:w-1/2 relative flex items-center justify-center">

          {/* LEFT ARROW */}
          <button
            onClick={handlePrev}
            className="
              absolute left-0 -ml-4 
              p-3 rounded-full 
              backdrop-blur-xl bg-white/50 border border-white/30 
              shadow-xl hover:scale-110 transition
            "
          >
            <ChevronLeft className="w-6 h-6 text-primary" />
          </button>

          {/* CARD */}
          <div className="w-full max-w-sm mx-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.94, y: 35 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: -35 }}
                transition={{ duration: 0.35 }}
                className="
                  bg-white/90 backdrop-blur-xl p-10 rounded-2xl shadow-xl 
                  hover:shadow-2xl transition relative overflow-hidden
                "
              >
                {/* SHINE HOVER EFFECT */}
                <div className="absolute inset-0 opacity-0 hover:opacity-10 transition bg-gradient-to-br from-white/40 to-transparent"></div>

                <div className="flex flex-col items-center space-y-6 text-center">
                  {steps[currentIndex].icon}

                  <h3 className="text-xl font-semibold text-primary">
                    {steps[currentIndex].text}
                  </h3>

                  <p className="text-primary-dark text-sm">
                    {steps[currentIndex].details}
                  </p>

                  <p className="text-primary/60 text-xs">
                    Step {currentIndex + 1} of {steps.length}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* PROGRESS DOTS */}
            <div className="flex justify-center mt-4 gap-2">
              {steps.map((_, idx) => (
                <motion.div
                  key={idx}
                  className={`w-3 h-3 rounded-full ${
                    idx === currentIndex ? "bg-primary" : "bg-primary/25"
                  }`}
                  animate={{
                    scale: idx === currentIndex ? 1.3 : 1,
                  }}
                />
              ))}
            </div>
          </div>

          {/* RIGHT ARROW */}
          <button
            onClick={handleNext}
            className="
              absolute right-0 -mr-4 
              p-3 rounded-full 
              backdrop-blur-xl bg-white/50 border border-white/30 
              shadow-xl hover:scale-110 transition
            "
          >
            <ChevronRight className="w-6 h-6 text-primary" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default JourneySteps;
